import { CreateAccountFields } from "../@types/CreateAccountFields";
import { ErrorCodes } from "../@types/ErrorCodes";
import { ErrorResponse, LoginRedirectResponse } from "../@types/api";

interface CreateAccountArgs extends CreateAccountFields {
  isFederated: boolean;
  invitationToken: string;
  termsAndConditionsVersion: string;
}

interface Result {
  status: string;
  code?: ErrorCodes;
  redirect?: string;
}

/**
 * Perform the API call to make an account given the information required.
 * @param {CreateAccountArgs} _args The arguments necessary for creating an account.
 * @returns A string which represents the redirect location on success
 */
export default async function createAccount(
  _args: CreateAccountArgs,
): Promise<Result> {
  try {
    const response = await callCompleteRegistration(_args);

    switch (response.status) {
      case 200:
        return handleSuccess(response);
      case 400:
        return handleError(response);
      default:
        return { status: "error", code: ErrorCodes.E0003 };
    }
  } catch (err) {
    console.error("Network error while creating account: ", err);
    return { status: "error", code: ErrorCodes.E0002 };
  }
}

async function callCompleteRegistration({
  firstName,
  lastName,
  email,
  password,
  passwordConfirmation,
  isFederated,
  invitationToken,
  termsAndConditionsVersion,
}: CreateAccountArgs): Promise<Response> {
  const url = new URL(window.REACT_APP_API_URL + `/complete-registration`);
  let payload;
  if (isFederated) {
    payload = {
      firstName,
      lastName,
      email,
      termsAndConditionsVersion,
    };
  } else {
    payload = {
      firstName,
      lastName,
      email,
      password,
      passwordConfirmation,
      termsAndConditionsVersion,
    };
  }

  return fetch(url, {
    method: "post",
    headers: new Headers({
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${invitationToken}`,
    }),
    body: JSON.stringify(payload),
    credentials: "include",
  });
}

async function handleSuccess(response: Response): Promise<Result> {
  const responseData = (await response.json()) as LoginRedirectResponse;
  return {
    status: "ok",
    redirect: responseData.loginRedirect,
  };
}

async function handleError(response: Response): Promise<Result> {
  const responseData = (await response.json()) as ErrorResponse;
  return {
    status: "error",
    code: responseData.errorCode,
  };
}
